/**
 * URL Parameter Utilities for DatePicker/DateRangePicker
 *
 * Provides consistent encoding/decoding of date range and timezone parameters
 * in URLs for shareable links and bookmark support.
 */

// =============================================================================
// Types
// =============================================================================

export interface DateRangeUrlParams {
  from: string | null;
  to: string | null;
  timezone: string | null;
}

export interface DateRangeUrlParamsWithDefaults {
  from: string | null;
  to: string | null;
  timezone: string;
}

export interface ParseUrlParamsOptions {
  /** Default from value if not in URL */
  defaultFrom?: string | null;
  /** Default to value if not in URL */
  defaultTo?: string | null;
  /** Default timezone if not in URL (defaults to "UTC") */
  defaultTimezone?: string;
  /** Key for from param (defaults to "from") */
  fromKey?: string;
  /** Key for to param (defaults to "to") */
  toKey?: string;
  /** Key for timezone param (defaults to "tz") */
  timezoneKey?: string;
}

export interface BuildUrlParamsOptions {
  /** Key for from param (defaults to "from") */
  fromKey?: string;
  /** Key for to param (defaults to "to") */
  toKey?: string;
  /** Key for timezone param (defaults to "tz") */
  timezoneKey?: string;
  /** If true, omit timezone param when it equals this value */
  omitTimezoneWhenDefault?: string;
}

// =============================================================================
// URL Parameter Keys (defaults)
// =============================================================================

export const URL_PARAM_KEYS = {
  from: "from",
  to: "to",
  timezone: "tz",
} as const;

// =============================================================================
// Parsing Functions
// =============================================================================

/**
 * Parse date range and timezone from URLSearchParams
 *
 * @param searchParams - URLSearchParams object from useSearchParams()
 * @param options - Parsing options with defaults
 * @returns Parsed date range and timezone values
 *
 * @example
 * ```tsx
 * const [searchParams] = useSearchParams();
 * const { from, to, timezone } = parseDateRangeFromUrl(searchParams, {
 *   defaultFrom: "now-7d",
 *   defaultTo: null,
 *   defaultTimezone: "UTC",
 * });
 * ```
 */
export function parseDateRangeFromUrl(
  searchParams: URLSearchParams,
  options: ParseUrlParamsOptions = {}
): DateRangeUrlParamsWithDefaults {
  const {
    defaultFrom = null,
    defaultTo = null,
    defaultTimezone = "UTC",
    fromKey = URL_PARAM_KEYS.from,
    toKey = URL_PARAM_KEYS.to,
    timezoneKey = URL_PARAM_KEYS.timezone,
  } = options;

  const fromParam = searchParams.get(fromKey);
  const toParam = searchParams.get(toKey);
  const tzParam = searchParams.get(timezoneKey);

  return {
    from: fromParam !== null ? fromParam : defaultFrom,
    to: toParam !== null ? toParam : defaultTo,
    timezone: tzParam !== null ? tzParam : defaultTimezone,
  };
}

/**
 * Parse date range and timezone from a query string (using qs library format)
 *
 * @param queryObject - Parsed query object (from qs.parse)
 * @param options - Parsing options with defaults
 * @returns Parsed date range and timezone values
 *
 * @example
 * ```tsx
 * const queryObject = qs.parse(location.search, { ignoreQueryPrefix: true });
 * const { from, to, timezone } = parseDateRangeFromQueryObject(queryObject, {
 *   defaultFrom: "now-7d",
 *   defaultTimezone: "UTC",
 * });
 * ```
 */
export function parseDateRangeFromQueryObject(
  queryObject: Record<string, unknown>,
  options: ParseUrlParamsOptions = {}
): DateRangeUrlParamsWithDefaults {
  const {
    defaultFrom = null,
    defaultTo = null,
    defaultTimezone = "UTC",
    fromKey = URL_PARAM_KEYS.from,
    toKey = URL_PARAM_KEYS.to,
    timezoneKey = URL_PARAM_KEYS.timezone,
  } = options;

  const fromParam = queryObject[fromKey];
  const toParam = queryObject[toKey];
  const tzParam = queryObject[timezoneKey];

  return {
    from: typeof fromParam === "string" ? fromParam : defaultFrom,
    to: typeof toParam === "string" ? toParam : defaultTo,
    timezone: typeof tzParam === "string" ? tzParam : defaultTimezone,
  };
}

// =============================================================================
// Building Functions
// =============================================================================

/**
 * Build URLSearchParams with date range and timezone
 *
 * @param params - Date range and timezone values
 * @param existingParams - Optional existing URLSearchParams to extend
 * @param options - Building options
 * @returns URLSearchParams with date range and timezone
 *
 * @example
 * ```tsx
 * const params = buildDateRangeUrlParams(
 *   { from: "now-7d", to: null, timezone: "UTC" },
 *   new URLSearchParams(), // existing params
 *   { omitTimezoneWhenDefault: "UTC" }
 * );
 * setSearchParams(params);
 * ```
 */
export function buildDateRangeUrlParams(
  params: DateRangeUrlParams,
  existingParams?: URLSearchParams,
  options: BuildUrlParamsOptions = {}
): URLSearchParams {
  const {
    fromKey = URL_PARAM_KEYS.from,
    toKey = URL_PARAM_KEYS.to,
    timezoneKey = URL_PARAM_KEYS.timezone,
    omitTimezoneWhenDefault,
  } = options;

  const searchParams = existingParams ? new URLSearchParams(existingParams) : new URLSearchParams();

  // Set or delete from param
  if (params.from) {
    searchParams.set(fromKey, params.from);
  } else {
    searchParams.delete(fromKey);
  }

  // Set or delete to param
  if (params.to) {
    searchParams.set(toKey, params.to);
  } else {
    searchParams.delete(toKey);
  }

  // Set or delete timezone param
  if (params.timezone && params.timezone !== omitTimezoneWhenDefault) {
    searchParams.set(timezoneKey, params.timezone);
  } else {
    searchParams.delete(timezoneKey);
  }

  return searchParams;
}

/**
 * Build a query object for use with qs.stringify
 *
 * @param params - Date range and timezone values
 * @param existingObject - Optional existing query object to extend
 * @param options - Building options
 * @returns Query object with date range and timezone
 *
 * @example
 * ```tsx
 * const qsObject = buildDateRangeQueryObject(
 *   { from: "now-7d", to: null, timezone: "America/New_York" },
 *   { kind: "workload", name: "my-app" }
 * );
 * const queryString = qs.stringify(qsObject);
 * navigate({ search: `?${queryString}` });
 * ```
 */
export function buildDateRangeQueryObject(
  params: DateRangeUrlParams,
  existingObject?: Record<string, unknown>,
  options: BuildUrlParamsOptions = {}
): Record<string, unknown> {
  const {
    fromKey = URL_PARAM_KEYS.from,
    toKey = URL_PARAM_KEYS.to,
    timezoneKey = URL_PARAM_KEYS.timezone,
    omitTimezoneWhenDefault,
  } = options;

  const result: Record<string, unknown> = { ...existingObject };

  // Set or delete from param
  if (params.from) {
    result[fromKey] = params.from;
  } else {
    delete result[fromKey];
  }

  // Set or delete to param
  if (params.to) {
    result[toKey] = params.to;
  } else {
    delete result[toKey];
  }

  // Set or delete timezone param
  if (params.timezone && params.timezone !== omitTimezoneWhenDefault) {
    result[timezoneKey] = params.timezone;
  } else {
    delete result[timezoneKey];
  }

  return result;
}

// =============================================================================
// Timezone Helpers
// =============================================================================

/**
 * Convert timezone value to URL-safe format
 * "UTC" stays as "UTC", local timezone becomes "local"
 *
 * @param timezone - Timezone value (IANA string like "America/New_York" or "UTC")
 * @param localTimezone - The browser's local timezone for comparison
 * @returns URL-safe timezone string ("UTC" or "local")
 */
export function timezoneToUrlValue(timezone: string, localTimezone: string): string {
  if (timezone === "UTC" || timezone === "Etc/UTC") {
    return "UTC";
  }
  // Any non-UTC timezone is stored as "local" since we only support UTC/Local toggle
  return "local";
}

/**
 * Convert URL timezone value back to actual timezone
 *
 * @param urlValue - URL timezone value ("UTC" or "local")
 * @param localTimezone - The browser's local timezone
 * @returns Actual timezone string
 */
export function urlValueToTimezone(urlValue: string | null, localTimezone: string): string {
  if (!urlValue || urlValue === "UTC") {
    return "UTC";
  }
  if (urlValue === "local") {
    return localTimezone;
  }
  // Support direct IANA timezone strings as well
  return urlValue;
}

// =============================================================================
// React Hook Helper
// =============================================================================

/**
 * Helper to create URL update function for date range changes
 *
 * @param setSearchParams - setSearchParams from useSearchParams()
 * @param options - Building options
 * @returns Function to update URL with new date range values
 *
 * @example
 * ```tsx
 * const [searchParams, setSearchParams] = useSearchParams();
 * const updateDateRangeUrl = createDateRangeUrlUpdater(setSearchParams);
 *
 * // Later, when values change:
 * updateDateRangeUrl({ from: newFrom, to: newTo, timezone: newTimezone });
 * ```
 */
export function createDateRangeUrlUpdater(
  setSearchParams: (params: URLSearchParams) => void,
  options: BuildUrlParamsOptions = {}
): (params: DateRangeUrlParams, existingParams?: URLSearchParams) => void {
  return (params: DateRangeUrlParams, existingParams?: URLSearchParams) => {
    const newParams = buildDateRangeUrlParams(params, existingParams, options);
    setSearchParams(newParams);
  };
}
